/** Every HTTP route lives below this prefix so nginx and the Vite dev proxy need one rule. */
export const API_PREFIX = '/api'

export const apiRoutes = {
  health: `${API_PREFIX}/health`,
  events: `${API_PREFIX}/events`,
  vaultStatus: `${API_PREFIX}/vault/status`,
  vaultSetup: `${API_PREFIX}/vault/setup`,
  vaultUnlock: `${API_PREFIX}/vault/unlock`,
  vaultLock: `${API_PREFIX}/vault/lock`,
  vaultAutoUnlock: `${API_PREFIX}/vault/auto-unlock`,
  totpStatus: `${API_PREFIX}/auth/totp`,
  totpSetup: `${API_PREFIX}/auth/totp/setup`,
  totpConfirm: `${API_PREFIX}/auth/totp/confirm`,
  totpDisable: `${API_PREFIX}/auth/totp/disable`,
  configExport: `${API_PREFIX}/config/export`,
  configImport: `${API_PREFIX}/config/import`,
  remoteInstance: `${API_PREFIX}/config/remote`,
  servers: `${API_PREFIX}/servers`,
  reachability: `${API_PREFIX}/reachability`,
  maintenanceReport: `${API_PREFIX}/reports/maintenance`,
  fileVersions: `${API_PREFIX}/files/versions`,
  connections: `${API_PREFIX}/connections`,
  server: (id: string) => `${API_PREFIX}/servers/${encodeURIComponent(id)}`,
  serverTestConnection: (id: string) =>
    `${API_PREFIX}/servers/${encodeURIComponent(id)}/test-connection`,
  serverTrustHostKey: (id: string) =>
    `${API_PREFIX}/servers/${encodeURIComponent(id)}/trust-host-key`,
  serverConnect: (id: string) => `${API_PREFIX}/servers/${encodeURIComponent(id)}/connect`,
  serverDisconnect: (id: string) => `${API_PREFIX}/servers/${encodeURIComponent(id)}/disconnect`,
  serverChecklist: (id: string) => `${API_PREFIX}/servers/${encodeURIComponent(id)}/checklist`,
  serverFacts: (id: string) => `${API_PREFIX}/servers/${encodeURIComponent(id)}/facts`,
  serverWorkloads: (id: string) => `${API_PREFIX}/servers/${encodeURIComponent(id)}/workloads`,
  serverWorkloadAction: (id: string) =>
    `${API_PREFIX}/servers/${encodeURIComponent(id)}/workloads/action`,
  serverLogSocket: (id: string) => `${API_PREFIX}/servers/${encodeURIComponent(id)}/logs/socket`,
  projects: `${API_PREFIX}/projects`,
  project: (id: string) => `${API_PREFIX}/projects/${encodeURIComponent(id)}`,
  projectEnvironments: (id: string) =>
    `${API_PREFIX}/projects/${encodeURIComponent(id)}/environments`,
  projectEnvironment: (id: string, environmentId: string) =>
    `${API_PREFIX}/projects/${encodeURIComponent(id)}/environments/${encodeURIComponent(environmentId)}`,
  projectReleases: (id: string) => `${API_PREFIX}/projects/${encodeURIComponent(id)}/releases`,
  projectLock: (id: string) => `${API_PREFIX}/projects/${encodeURIComponent(id)}/lock`,
  previewUpdate: (id: string) => `${API_PREFIX}/projects/${encodeURIComponent(id)}/preview/update`,
  previewRefreshData: (id: string) =>
    `${API_PREFIX}/projects/${encodeURIComponent(id)}/preview/refresh-data`,
  projectDeployOverview: (id: string) =>
    `${API_PREFIX}/projects/${encodeURIComponent(id)}/deploy-overview`,
  projectDeploy: (id: string) => `${API_PREFIX}/projects/${encodeURIComponent(id)}/deploy`,
  projectDeployCancel: (id: string) =>
    `${API_PREFIX}/projects/${encodeURIComponent(id)}/deploy/cancel`,
  projectDeploySocket: (id: string) =>
    `${API_PREFIX}/projects/${encodeURIComponent(id)}/deploy/socket`,
  serverCertificates: (id: string) =>
    `${API_PREFIX}/servers/${encodeURIComponent(id)}/certificates`,
  serverCertbot: (id: string) => `${API_PREFIX}/servers/${encodeURIComponent(id)}/certbot`,
  serverServices: (id: string) => `${API_PREFIX}/servers/${encodeURIComponent(id)}/services`,
  serverNginxReload: (id: string) =>
    `${API_PREFIX}/servers/${encodeURIComponent(id)}/services/nginx/reload`,
  serverSecurity: (id: string) => `${API_PREFIX}/servers/${encodeURIComponent(id)}/security`,
  serverReboot: (id: string) => `${API_PREFIX}/servers/${encodeURIComponent(id)}/reboot`,
  serverUpdates: (id: string) => `${API_PREFIX}/servers/${encodeURIComponent(id)}/updates`,
  serverUpdatesRun: (id: string) => `${API_PREFIX}/servers/${encodeURIComponent(id)}/updates/run`,
  backupJobs: `${API_PREFIX}/backups/jobs`,
  backupJob: (id: string) => `${API_PREFIX}/backups/jobs/${encodeURIComponent(id)}`,
  backupJobRun: (id: string) => `${API_PREFIX}/backups/jobs/${encodeURIComponent(id)}/run`,
  backupJobRuns: (id: string) => `${API_PREFIX}/backups/jobs/${encodeURIComponent(id)}/runs`,
  overview: `${API_PREFIX}/overview`,
  monitoringStatus: `${API_PREFIX}/monitoring/status`,
  serverMetrics: (id: string) => `${API_PREFIX}/servers/${encodeURIComponent(id)}/metrics`,
  alertRules: `${API_PREFIX}/alerts/rules`,
  alertRule: (id: string) => `${API_PREFIX}/alerts/rules/${encodeURIComponent(id)}`,
  alertEvents: `${API_PREFIX}/alerts/events`,
  alertChannels: `${API_PREFIX}/alerts/channels`,
  alertChannel: (channel: string) => `${API_PREFIX}/alerts/channels/${channel}`,
  alertChannelTest: (channel: string) => `${API_PREFIX}/alerts/channels/${channel}/test`,
  snippets: `${API_PREFIX}/snippets`,
  snippet: (id: string) => `${API_PREFIX}/snippets/${encodeURIComponent(id)}`,
  snippetRun: (id: string) => `${API_PREFIX}/snippets/${encodeURIComponent(id)}/run`,
  filesDirectory: `${API_PREFIX}/files/directory`,
  filesMkdir: `${API_PREFIX}/files/directory/create`,
  filesRename: `${API_PREFIX}/files/rename`,
  filesDelete: `${API_PREFIX}/files/delete`,
  filesChmod: `${API_PREFIX}/files/chmod`,
  filesChown: `${API_PREFIX}/files/chown`,
  filesContent: `${API_PREFIX}/files/content`,
  serverBookmarks: (id: string) => `${API_PREFIX}/servers/${encodeURIComponent(id)}/bookmarks`,
  serverBookmark: (id: string, bookmarkId: string) =>
    `${API_PREFIX}/servers/${encodeURIComponent(id)}/bookmarks/${encodeURIComponent(bookmarkId)}`,
  transfers: `${API_PREFIX}/transfers`,
  transferCancel: (id: string) => `${API_PREFIX}/transfers/${encodeURIComponent(id)}/cancel`,
  transferRetry: (id: string) => `${API_PREFIX}/transfers/${encodeURIComponent(id)}/retry`,
  transferResolve: (id: string) => `${API_PREFIX}/transfers/${encodeURIComponent(id)}/resolve`,
  terminalSessions: `${API_PREFIX}/terminal/sessions`,
  terminalSession: (id: string) => `${API_PREFIX}/terminal/sessions/${encodeURIComponent(id)}`,
  terminalSocket: (id: string) =>
    `${API_PREFIX}/terminal/sessions/${encodeURIComponent(id)}/socket`,
} as const
